/*
 * Copyright 2022, CS GROUP - France, https://www.csgroup.eu/
 *
 * This file is part of ToPaZ project: http://www.github.com/CS-SI/ToPaZ
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/*
 * Date Format 1.2.3
 * (c) 2007-2009 Steven Levithan <stevenlevithan.com>
 * MIT license
 *
 * Includes enhancements by Scott Trenda <scott.trenda.net>
 * and Kris Kowal <cixar.com/~kris.kowal/>
 *
 * Accepts a date, a mask, or a date and a mask.
 * Returns a formatted version of the given date.
 * The date defaults to the current date/time.
 * The mask defaults to dateFormat.masks.default.
 *
 * jan 2020: Converted by Jérémie Turbet (CSGroup) for Typescript
 * jeremie.turbet@c-s.fr
 */

export class DateFormat {
    private static readonly token = /d{1,4}|m{1,4}|yy(?:yy)?|([HhMsTt])\1?|[LloSZ]|"[^"]*"|'[^']*'/g;
    private static readonly timezone =
        /\b(?:[PMCEA][SDP]T|(?:Pacific|Mountain|Central|Eastern|Atlantic) (?:Standard|Daylight|Prevailing) Time|(?:GMT|UTC)(?:[-+]\d{4})?)\b/g;
    private static readonly timezoneClip = /[^-+\dA-Z]/g;

    // Some common format strings
    public static readonly masks: { [id: string]: string } = {
        default: 'ddd mmm dd yyyy HH:MM:ss',
        shortDate: 'm/d/yy',
        mediumDate: 'mmm d, yyyy',
        longDate: 'mmmm d, yyyy',
        fullDate: 'dddd, mmmm d, yyyy',
        shortTime: 'h:MM TT',
        mediumTime: 'h:MM:ss TT',
        longTime: 'h:MM:ss TT Z',
        isoDate: 'yyyy-mm-dd',
        isoTime: 'HH:MM:ss',
        isoDateTime: "yyyy-mm-dd'T'HH:MM:ss",
        isoUtcDateTime: "UTC:yyyy-mm-dd'T'HH:MM:ss'Z'"
    };

    // Internationalization strings
    private static readonly dayNames: { [lang: string]: string[] } = {
        en: [
            'Sun',
            'Mon',
            'Tue',
            'Wed',
            'Thu',
            'Fri',
            'Sat',
            'Sunday',
            'Monday',
            'Tuesday',
            'Wednesday',
            'Thursday',
            'Friday',
            'Saturday'
        ],
        fr: [
            'Dim',
            'Lun',
            'Mar',
            'Merc',
            'Jeu',
            'Ven',
            'Sam',
            'Dimanche',
            'Lundi',
            'Mardi',
            'Mercredi',
            'Jeudi',
            'Vendredi',
            'Samedi'
        ]
    };
    private static readonly monthNames: { [lang: string]: string[] } = {
        en: [
            'Jan',
            'Feb',
            'Mar',
            'Apr',
            'May',
            'Jun',
            'Jul',
            'Aug',
            'Sep',
            'Oct',
            'Nov',
            'Dec',
            'January',
            'February',
            'March',
            'April',
            'May',
            'June',
            'July',
            'August',
            'September',
            'October',
            'November',
            'December'
        ],
        fr: [
            'Jan',
            'Fev',
            'Mar',
            'Avr',
            'Mai',
            'Jun',
            'Jul',
            'Aou',
            'Sep',
            'Oct',
            'Nov',
            'Dec',
            'Janvier',
            'Fevrier',
            'Mars',
            'Avril',
            'Mai',
            'Juin',
            'Juillet',
            'Aout',
            'Septembre',
            'Octobre',
            'Novembre',
            'Decembre'
        ]
    };
    private static readonly lang: string = 'fr';

    /**
     * DateFormat.pad string with characters (fills with characters "0")
     * @param val string to DateFormat.pad
     * @param len DateFormat.pad length
     */
    private static pad(val: number, len: number = 2): string {
        let strVal = String(val);
        while (strVal.length < len) strVal = '0' + strVal;
        return strVal;
    }

    // Regexes and supporting functions are cached through closure
    public static format(date: Date, mask: string = DateFormat.masks['default'], utc: boolean = false): string {
        const lang: string = DateFormat?.lang || 'en';
        if (!DateFormat.dayNames[lang]) return 'invalid lang ' + lang + ' for dayNames';
        if (!DateFormat.monthNames[lang]) return 'invalid lang ' + lang + ' for monthNames';

        // Passing date through Date applies Date.parse, if necessary
        if (!date) throw new SyntaxError('invalid date');

        if (mask in DateFormat.masks) mask = DateFormat.masks[mask];

        // Allow setting the utc argument via the mask
        if (mask.slice(0, 4) === 'UTC:') {
            mask = mask.slice(4);
            utc = true;
        }

        const d: number = utc ? date.getUTCDate() : date.getDate();
        const D: number = utc ? date.getUTCDay() : date.getDay();
        const m: number = utc ? date.getUTCMonth() : date.getMonth();
        const y: number = utc ? date.getUTCFullYear() : date.getFullYear();
        const H: number = utc ? date.getUTCHours() : date.getHours();
        const M: number = utc ? date.getUTCMinutes() : date.getMinutes();
        const s: number = utc ? date.getUTCSeconds() : date.getSeconds();
        const L: number = utc ? date.getUTCMilliseconds() : date.getMilliseconds();
        const o: number = utc ? 0 : date.getTimezoneOffset();
        const flags: any = {
            d: d,
            dd: DateFormat.pad(d),
            ddd: DateFormat.dayNames[lang][D],
            dddd: DateFormat.dayNames[lang][D + 7],
            m: m + 1,
            mm: DateFormat.pad(m + 1),
            mmm: DateFormat.monthNames[lang][m],
            mmmm: DateFormat.monthNames[lang][m + 12],
            yy: String(y).slice(2),
            yyyy: y,
            h: H % 12 || 12,
            hh: DateFormat.pad(H % 12 || 12),
            H: H,
            HH: DateFormat.pad(H),
            M: M,
            MM: DateFormat.pad(M),
            s: s,
            ss: DateFormat.pad(s),
            l: DateFormat.pad(L, 3),
            L: DateFormat.pad(L > 99 ? Math.round(L / 10) : L),
            t: H < 12 ? 'a' : 'p',
            tt: H < 12 ? 'am' : 'pm',
            T: H < 12 ? 'A' : 'P',
            TT: H < 12 ? 'AM' : 'PM',
            Z: utc
                ? 'UTC'
                : (String(date).match(DateFormat.timezone) || ['']).pop().replace(DateFormat.timezoneClip, ''),
            o: (o > 0 ? '-' : '+') + DateFormat.pad(Math.floor(Math.abs(o) / 60) * 100 + (Math.abs(o) % 60), 4),
            S: ['th', 'st', 'nd', 'rd'][d % 10 > 3 ? 0 : (Number((d % 100) - (d % 10) !== 10) * d) % 10]
        };

        return mask.replace(DateFormat.token, function ($0) {
            return $0 in flags ? flags[$0] : $0.slice(1, $0.length - 1);
        });
    }
}
